import React, { useState } from 'react';
import { Eye } from 'lucide-react';
import { Panel } from '../../common/Panel';
import { InfoItem } from '../../common/InfoItem';
import { PersonPhoto } from '../../common/PersonPhoto';
import { ReviewChecklistPanel } from './ReviewChecklistPanel';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import type { PreviewDoc } from './DocumentPreviewModal';
import { useOwnerService } from '../../../contexts/OwnerServiceContext';
import { useApplicationReview } from '../../../contexts/ApplicationReviewContext';
import { ownerChecklist } from '../../../data/applicationReview';
import { ownerImages } from '../../../data/ownerProfile';
import { images } from '../../../data/images';
import { can } from '../../../utils/applicationQueue';
import { ownerInfo } from '../../../utils/applicationRecords';
import type { OwnerApplication } from '../../../types/owner';
export function OwnerReviewTab({
  app
}: {
  app: OwnerApplication;
}) {
  const {
    registrations
  } = useOwnerService();
  const {
    getReview,
    updateReview,
    addHistory,
    role
  } = useApplicationReview();
  const [preview, setPreview] = useState<PreviewDoc | null>(null);
  const o = ownerInfo(app, registrations);
  const r = getReview(app);
  const editable = can(role, 'review') || can(role, 'validate');
  const docs: PreviewDoc[] = [{
    name: 'মালিকের ছবি',
    src: o.photo ?? images.owner,
    meta: [{
      label: 'নাম',
      value: o.name
    }, {
      label: 'আপলোড',
      value: app.submitted
    }]
  }, {
    name: 'NID সামনের অংশ',
    src: ownerImages.docScan,
    meta: [{
      label: 'NID নম্বর',
      value: o.nid
    }, {
      label: 'জন্মতারিখ',
      value: o.dob
    }]
  }, {
    name: 'NID পেছনের অংশ',
    src: ownerImages.docScan,
    meta: [{
      label: 'ঠিকানা',
      value: o.address
    }, {
      label: 'ইস্যুকারী',
      value: 'বাংলাদেশ নির্বাচন কমিশন'
    }]
  }];
  return <div className="grid gap-4 xl:grid-cols-5" data-id="element-457" data-mp="39wnpdy6csltb">
      <div className="space-y-4 xl:col-span-3" data-id="element-458" data-mp="39vcl7x8zxc1o">
        <Panel title="মালিকের তথ্য যাচাই" subtitle="মালিকের মোবাইল অ্যাপে জমাকৃত প্রোফাইল" data-id="element-459" data-mp="34y2wkjtfl4ew">
          <div className="flex flex-col gap-5 sm:flex-row" data-id="element-460" data-mp="22koma38yda5m">
            <PersonPhoto src={o.photo} name={o.name} size={96} rounded="md" data-id="element-461" data-mp="22k53ylfe4jr2" />
            <dl className="grid flex-1 gap-x-6 gap-y-4 sm:grid-cols-2" data-id="element-462" data-mp="27i6l30t9a8zw">
              <InfoItem label="নাম" value={o.name} data-id="element-463" data-mp="2bkl71ts81ove" />
              <InfoItem label="পিতার নাম" value={o.father} data-id="element-464" data-mp="2bj09rweivrtu" />
              <InfoItem label="মাতার নাম" value={o.mother} data-id="element-465" data-mp="2gj4rfpr62sln" />
              <InfoItem label="NID" value={o.nid} data-id="element-466" data-mp="1jtx6ojydgnis" />
              <InfoItem label="জন্মতারিখ" value={o.dob} data-id="element-467" data-mp="1jv85s6gvh73k" />
              <InfoItem label="মোবাইল" value={<span data-id="element-469" data-mp="1nwjyikv1m8fz">{o.mobile} <span className="ml-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10.5px] font-semibold text-emerald-700" data-id="element-470" data-mp="1nw1uh2252pxi">OTP যাচাইকৃত</span></span>} data-id="element-468" data-mp="1nv8vwodc36qg" />
              <InfoItem label="ঠিকানা" value={o.address} data-id="element-471" data-mp="1xl1yizvi0bik" />
              <InfoItem label="মালিকানার ধরন" value="একক মালিকানা" data-id="element-472" data-mp="1xmmtgqlq5wgy" />
            </dl>
          </div>
        </Panel>
        <Panel title="মালিকের ডকুমেন্ট" subtitle="প্রিভিউ করতে ক্লিক করুন" data-id="element-473" data-mp="1043j6i2owukd">
          <div className="grid gap-3 sm:grid-cols-3" data-id="element-474" data-mp="153g1qqmj3fh5">
            {docs.map(d => <button key={d.name} onClick={() => setPreview(d)} className="group overflow-hidden rounded-md border border-border text-left transition hover:border-navy-300 hover:shadow" data-id="element-475" data-mp="xk6xtcwsk8zh">
                <div className="relative h-32 bg-slate-100" data-id="element-476" data-mp="nmanuoflbk2y">
                  <img src={d.src} alt={d.name} className="h-full w-full object-cover" data-id="element-477" data-mp="jm0h39jz4pn1" />
                  <span className="absolute inset-0 flex items-center justify-center bg-navy-900/0 text-white opacity-0 transition group-hover:bg-navy-900/40 group-hover:opacity-100" data-id="element-478" data-mp="emnixsnnjw55"><Eye className="h-6 w-6" data-id="element-479" data-mp="9uclcwgdwmfr" /></span>
                </div>
                <p className="px-3 py-2 text-sm font-medium text-navy-900" data-id="element-480" data-mp="4w2kq6bw4tmr">{d.name}</p>
              </button>)}
          </div>
        </Panel>
      </div>
      <div className="xl:col-span-2" data-id="element-481" data-mp="3s1k4vt1vlfup">
        <ReviewChecklistPanel title="যাচাই চেকলিস্ট — মালিক" items={ownerChecklist} checks={r.ownerChecks} result={r.ownerResult} disabled={!editable} onChange={c => updateReview(app, {
        ownerChecks: c
      })} onResult={res => {
        updateReview(app, {
          ownerResult: res
        });
        addHistory(app, 'মালিকের তথ্য যাচাই', res, res === 'যাচাইকৃত' ? 'green' : res === 'তথ্যের অমিল' ? 'red' : 'orange');
      }} data-id="element-482" data-mp="3s0ijovr5bcz9" />
      </div>
      <DocumentPreviewModal doc={preview} onClose={() => setPreview(null)} data-id="element-483" data-mp="1m7ayvet05gqd" />
    </div>;
}